<template>
<v-app>
  <div class="dash-page">
    <!-- ===== Page header ===== -->
    <div class="dash-head">
      <div>
        <h1 class="dash-title">Dashboard</h1>
        <p class="dash-sub">
          Welcome back, {{ userName }}<span v-if="districtLabel"> &middot; {{ districtLabel }}</span>
        </p>
      </div>
      <div class="dash-head-right">
        <v-select
          v-model="selectedDistrict"
          :items="districtOptions"
          item-text="district_name"
          item-value="district_name"
          label="District"
          prepend-inner-icon="mdi-map-marker-outline"
          outlined
          dense
          hide-details
          clearable
          class="dash-district-filter"
          @change="fetchDashboardStats"
        />
        <div class="dash-date">
          <v-icon small color="#5b6b84" class="mr-1">mdi-calendar</v-icon>
          {{ today }}
        </div>
      </div>
    </div>

    <!-- ===== Hero KPI cards ===== -->
    <v-row class="mt-1">
      <v-col cols="12" sm="6" lg="3" v-for="s in heroStats" :key="s.label">
        <v-card class="kpi-card" elevation="0">
          <div class="kpi-top">
            <div class="kpi-icon" :style="{ background: s.bg }">
              <v-icon :color="s.color">{{ s.icon }}</v-icon>
            </div>
            <div class="kpi-text">
              <span class="kpi-value">{{ s.value }}</span>
              <span class="kpi-label">{{ s.label }}</span>
            </div>
          </div>
          <div class="kpi-foot" :style="{ color: s.color }">{{ s.hint }}</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- ===== Alert strip ===== -->
    <v-row v-if="overdueFollowup > 0 || sickChildren > 0" class="mt-2">
      <v-col cols="12">
        <div class="alert-strip">
          <v-icon color="#c62828" class="mr-2">mdi-alert-circle-outline</v-icon>
          <span>
            <strong>{{ overdueFollowup }}</strong> overdue follow-up{{ overdueFollowup === 1 ? '' : 's' }}
            <template v-if="sickChildren > 0">
              &nbsp;&middot;&nbsp; <strong>{{ sickChildren }}</strong> sick child{{ sickChildren === 1 ? '' : 'ren' }} under care
            </template>
          </span>
        </div>
      </v-col>
    </v-row>

    <!-- ===== Overview grid ===== -->
    <div class="section-head">
      <h2 class="section-title">Overview</h2>
      <span class="section-sub">{{ selectedDistrict ? 'Figures for ' + selectedDistrict : 'All figures are scoped to your assigned district' + (districtLabel ? 's' : '') }}</span>
    </div>

    <v-row dense>
      <v-col cols="6" sm="4" md="3" lg="2" v-for="s in gridStats" :key="s.label">
        <v-card class="stat-tile" elevation="0">
          <div class="stat-icon-wrap" :style="{ background: s.bg }">
            <v-icon small :color="s.color">{{ s.icon }}</v-icon>
          </div>
          <div class="stat-value">{{ s.value }}</div>
          <div class="stat-label">{{ s.label }}</div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</v-app>
</template>

<script>
// @ is an alias to /src

import axios from 'axios';

// Single stats endpoint used by the dashboard
const API_URL_STATS = process.env.VUE_APP_API_URL;

export default {
 data(){
  return{
    items:[
      {account:'Salam',money:'200'},
      {account:'IBS',money:'3000'},
      {account:'E-Dahab',money:'20'}, 
    ],
    authenticated:JSON.parse(localStorage.getItem('status')),
    op:0,
    allacounts:[],
    byaccount:[],
    bybranch:[],
    bydonstem:[],
    mothersCount: 0,
    childrenCount: 0,
    mothersMale: 0,
    mothersFemale: 0,
    childrenMale: 0,
    childrenFemale: 0,
    assignedChildren: 0,
    unassignedChildren: 0,
    transferredChildren: 0,
    licensedMothers: 0,
    unlicensedMothers: 0,
    totalNabadoon: 0,
    activeUsers: 0,
    totalAssignments: 0,
    totalLicences: 0,
    childrenThisMonth: 0,
    mothersThisMonth: 0,
    sickChildren: 0,
    sickChildrenThisMonth: 0,
    overdueFollowup: 0,
    districtOptions: [],
    selectedDistrict: '',
  }
 },
computed:{
  userName(){
    return localStorage.getItem('fname') || 'User';
  },
  districtLabel(){
    try{
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if(u.district) return u.district;
      if(Array.isArray(u.districts) && u.districts.length) return u.districts.join(', ');
    }catch(e){ /* ignore */ }
    return '';
  },
  today(){
    return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  },
  heroStats(){
    return [
      { label: 'Children',        value: this.childrenCount,     icon: 'mdi-human-child',        color: '#1565c0', bg: '#e3f0fd', hint: `${this.childrenThisMonth} new this month` },
      { label: 'Care Givers',     value: this.mothersCount,      icon: 'mdi-account-heart',      color: '#6a1b9a', bg: '#f3e5f5', hint: `${this.mothersThisMonth} new this month` },
      { label: 'Assigned Children', value: this.assignedChildren, icon: 'mdi-account-arrow-right', color: '#00838f', bg: '#e0f7fa', hint: `${this.unassignedChildren} awaiting assignment` },
      { label: 'Overdue Follow-Up', value: this.overdueFollowup, icon: 'mdi-alarm',              color: '#c62828', bg: '#fdecea', hint: 'Needs urgent attention' },
    ];
  },
  gridStats(){
    return [
      { label: 'Children - Male',        value: this.childrenMale,          icon: 'mdi-gender-male',          color: '#1565c0', bg: '#e3f0fd' },
      { label: 'Children - Female',      value: this.childrenFemale,        icon: 'mdi-gender-female',        color: '#ad1457', bg: '#fce4ec' },
      { label: 'Care Givers - Male',     value: this.mothersMale,           icon: 'mdi-gender-male',          color: '#1565c0', bg: '#e3f0fd' },
      { label: 'Care Givers - Female',   value: this.mothersFemale,         icon: 'mdi-gender-female',        color: '#ad1457', bg: '#fce4ec' },
      { label: 'Unassigned Children',    value: this.unassignedChildren,    icon: 'mdi-account-off',          color: '#ef6c00', bg: '#fff3e0' },
      { label: 'Transferred Children',   value: this.transferredChildren,   icon: 'mdi-account-switch',       color: '#5e35b1', bg: '#ede7f6' },
      { label: 'Licensed Care Givers',   value: this.licensedMothers,       icon: 'mdi-certificate',          color: '#2e7d32', bg: '#e8f5e9' },
      { label: 'Unlicensed Care Givers', value: this.unlicensedMothers,     icon: 'mdi-certificate-outline',  color: '#ef6c00', bg: '#fff3e0' },
      { label: 'Clan Elders',            value: this.totalNabadoon,         icon: 'mdi-account-tie',          color: '#00695c', bg: '#e0f2f1' },
      { label: 'Active Users',           value: this.activeUsers,           icon: 'mdi-account-multiple',     color: '#37474f', bg: '#eceff1' },
      { label: 'Total Assignments',      value: this.totalAssignments,      icon: 'mdi-clipboard-check',      color: '#00838f', bg: '#e0f7fa' },
      { label: 'Total Licences',         value: this.totalLicences,         icon: 'mdi-file-document',        color: '#4527a0', bg: '#ede7f6' },
      { label: 'Children This Month',    value: this.childrenThisMonth,     icon: 'mdi-calendar-plus',        color: '#1565c0', bg: '#e3f0fd' },
      { label: 'Care Givers This Month', value: this.mothersThisMonth,      icon: 'mdi-calendar-plus',        color: '#6a1b9a', bg: '#f3e5f5' },
      { label: 'Sick Children',          value: this.sickChildren,          icon: 'mdi-hospital-box',         color: '#c62828', bg: '#fdecea' },
      { label: 'Sick This Month',        value: this.sickChildrenThisMonth, icon: 'mdi-calendar-alert',       color: '#c62828', bg: '#fdecea' },
    ];
  },
},
methods:{
 
  // Load district list for the filter dropdown
  fetchDistricts(){
    axios.post(API_URL_STATS, { opcion: 40 })
      .then(res => { this.districtOptions = res.data || []; })
      .catch(() => { this.districtOptions = []; });
  },

  // Fetch all dashboard stats from single backend endpoint (opcion: 19)
  fetchDashboardStats(){
    axios.post(API_URL_STATS, { opcion: 19, usr_id: (JSON.parse(localStorage.getItem('user') || '{}')).id || '', district: this.selectedDistrict || '' })
      .then(res => {
        const payload = res.data || {};
        if(payload.success && payload.data){
          const d = payload.data;
          this.childrenCount   = Number(d.total_children)   || 0;
          this.childrenMale    = Number(d.child_male)       || 0;
          this.childrenFemale  = Number(d.child_female)     || 0;
          this.mothersCount    = Number(d.total_mothers)    || 0;
          this.mothersMale     = Number(d.mother_male)      || 0;
          this.mothersFemale   = Number(d.mother_female)    || 0;
          this.assignedChildren   = Number(d.assigned_children)      || 0;
          this.unassignedChildren = Number(d.unassigned_children)    || 0;
          this.transferredChildren = Number(d.transferred_children)  || 0;
          this.licensedMothers    = Number(d.licensed_mothers)      || 0;
          this.unlicensedMothers  = Number(d.unlicensed_mothers)    || 0;
          this.totalNabadoon      = Number(d.total_nabadoon)        || 0;
          this.activeUsers        = Number(d.active_users)          || 0;
          this.totalAssignments   = Number(d.total_assignments)     || 0;
          this.totalLicences         = Number(d.total_licences)         || 0;
          this.childrenThisMonth     = Number(d.children_this_month)    || 0;
          this.mothersThisMonth      = Number(d.mothers_this_month)     || 0;
          this.sickChildren          = Number(d.sickchild)              || 0;
          this.sickChildrenThisMonth = Number(d.sickchild_this_month)   || 0;
          this.overdueFollowup       = Number(d.overdue_followup)       || 0;
        } else {
          this.childrenCount = 0;
          this.childrenMale = 0;
          this.childrenFemale = 0;
          this.mothersCount = 0;
          this.mothersMale = 0;
          this.mothersFemale = 0;
          this.assignedChildren = 0;
          this.unassignedChildren = 0;
          this.transferredChildren = 0;
          this.licensedMothers = 0;
          this.unlicensedMothers = 0;
          this.totalNabadoon = 0;
          this.activeUsers = 0;
          this.totalAssignments = 0;
          this.totalLicences = 0;
          this.childrenThisMonth = 0;
          this.mothersThisMonth = 0;
          this.sickChildren = 0;
          this.sickChildrenThisMonth = 0;
          this.overdueFollowup = 0;
        }
      })
      .catch(() => {
        this.childrenCount = 0;
        this.childrenMale = 0;
        this.childrenFemale = 0;
        this.mothersCount = 0;
        this.mothersMale = 0;
        this.mothersFemale = 0;
        this.assignedChildren = 0;
        this.unassignedChildren = 0;
        this.transferredChildren = 0;
        this.licensedMothers = 0;
        this.unlicensedMothers = 0;
        this.totalNabadoon = 0;
        this.activeUsers = 0;
        this.totalAssignments = 0;
        this.totalLicences = 0;
        this.childrenThisMonth = 0;
        this.mothersThisMonth = 0;
        this.sickChildren = 0;
        this.sickChildrenThisMonth = 0;
        this.overdueFollowup = 0;
      });
  }
 },
 mounted(){
  this.fetchDistricts();
  this.fetchDashboardStats();
  if(!this.authenticated){
        this.$router.replace({name:'login'});
      }
//   const ctx = document.getElementById('myChart');
// const myChart = new Chart(ctx, {
//     type: 'bar',
//     data: {
//         labels: ['Red', 'Blue', 'Yellow', 'Green', 'Purple', 'Orange'],
//         datasets: [{
//             label: '# of Votes',
//             data: [12, 19, 3, 5, 2, 3],
//             backgroundColor: [
//                 'rgba(255, 99, 132, 0.2)',
//                 'rgba(54, 162, 235, 0.2)',
//                 'rgba(255, 206, 86, 0.2)',
//                 'rgba(75, 192, 192, 0.2)',
//                 'rgba(153, 102, 255, 0.2)',
//                 'rgba(255, 159, 64, 0.2)'
//             ],
//             borderColor: [
//                 'rgba(255, 99, 132, 1)',
//                 'rgba(54, 162, 235, 1)',
//                 'rgba(255, 206, 86, 1)',
//                 'rgba(75, 192, 192, 1)',
//                 'rgba(153, 102, 255, 1)',
//                 'rgba(255, 159, 64, 1)'
//             ],
//             borderWidth: 1
//         }]
//     },
//     options: {
//         scales: {
//             y: {
//                 beginAtZero: true
//             }
//         }
//     }
//     });
//      myChart;
//       const ctxbi = document.getElementById('mybichart');
//  const data = {
//   labels: [
//     'Red',
//     'Blue',
//     'Yellow'
//   ],
//   datasets: [{
//     label: 'My First Dataset',
//     data: [100, 50, 100],
//     backgroundColor: [
//       'rgb(255, 99, 132)',
//       'rgb(54, 162, 235)',
//       'rgb(255, 205, 86)'
//     ],
//     hoverOffset: 4
//   }]
// };
// const mybichart = new Chart(ctxbi, {
 
//   type: 'doughnut',
//   data: data,

// });
//  mybichart;  
//  }
 }
}
</script>

<style scoped>
.dash-page {
   padding: 28px 32px 40px;
   background: #f4f6fb;
   min-height: 100%;
}

/* ===== Header ===== */
.dash-head {
   display: flex;
   align-items: flex-end;
   justify-content: space-between;
   flex-wrap: wrap;
   gap: 10px;
   margin-bottom: 8px;
}
.dash-title {
   font-size: 1.6rem;
   font-weight: 800;
   color: #16233f;
   letter-spacing: -.4px;
}
.dash-sub {
   font-size: .88rem;
   color: #7a8699;
   margin: 2px 0 0;
}
.dash-date {
   display: flex;
   align-items: center;
   font-size: .82rem;
   color: #5b6b84;
   background: #fff;
   border: 1px solid #e6eaf2;
   border-radius: 10px;
   padding: 8px 14px;
   white-space: nowrap;
}
.dash-head-right {
   display: flex;
   align-items: center;
   gap: 12px;
   flex-wrap: wrap;
}
.dash-district-filter {
   width: 220px;
   background: #fff;
   border-radius: 10px;
}

/* ===== KPI cards ===== */
.kpi-card {
   border-radius: 16px !important;
   border: 1px solid #e6eaf2;
   padding: 20px;
   height: 100%;
   transition: box-shadow .2s ease, transform .15s ease;
}
.kpi-card:hover {
   box-shadow: 0 10px 26px rgba(22, 35, 63, .08) !important;
   transform: translateY(-2px);
}
.kpi-top {
   display: flex;
   align-items: center;
   gap: 16px;
}
.kpi-icon {
   width: 52px;
   height: 52px;
   border-radius: 14px;
   display: flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
}
.kpi-text {
   display: flex;
   flex-direction: column;
}
.kpi-value {
   font-size: 1.9rem;
   font-weight: 800;
   color: #16233f;
   line-height: 1.1;
}
.kpi-label {
   font-size: .8rem;
   font-weight: 600;
   color: #7a8699;
}
.kpi-foot {
   margin-top: 14px;
   padding-top: 12px;
   border-top: 1px dashed #e6eaf2;
   font-size: .74rem;
   font-weight: 600;
}

/* ===== Alert strip ===== */
.alert-strip {
   display: flex;
   align-items: center;
   background: #fdecea;
   border: 1px solid #f5c6c0;
   border-left: 4px solid #c62828;
   border-radius: 12px;
   padding: 12px 18px;
   font-size: .86rem;
   color: #7b241c;
}

/* ===== Section head ===== */
.section-head {
   display: flex;
   align-items: baseline;
   gap: 14px;
   margin: 26px 0 12px;
}
.section-title {
   font-size: 1.05rem;
   font-weight: 700;
   color: #26314a;
}
.section-sub {
   font-size: .76rem;
   color: #9aa5b5;
}

/* ===== Stat tiles ===== */
.stat-tile {
   border-radius: 14px !important;
   border: 1px solid #e6eaf2;
   padding: 16px 14px;
   text-align: center;
   height: 100%;
   transition: box-shadow .2s ease, transform .15s ease;
}
.stat-tile:hover {
   box-shadow: 0 8px 20px rgba(22, 35, 63, .07) !important;
   transform: translateY(-2px);
}
.stat-icon-wrap {
   width: 38px;
   height: 38px;
   border-radius: 10px;
   display: flex;
   align-items: center;
   justify-content: center;
   margin: 0 auto 10px;
}
.stat-value {
   font-size: 1.35rem;
   font-weight: 800;
   color: #16233f;
   line-height: 1.15;
}
.stat-label {
   font-size: .7rem;
   font-weight: 600;
   color: #7a8699;
   margin-top: 2px;
   line-height: 1.3;
}
</style>